import { faEnvelope } from '@fortawesome/pro-duotone-svg-icons'
import { faUndo } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import React, { createRef, useEffect, useState } from 'react'

interface IProps {
  value?: string
  has_error?: boolean
  disabled?: boolean
  onResend?: () => Promise<void> | void
  onChange?: (code: string) => Promise<void> | void
  onPrevious?: () => Promise<void> | void
  onComplete: (code: string) => Promise<void> | void
}

export const VerifyEmail = (props: IProps) => {
  const fields: number = 6
  let initialValues: string[] = Array(fields).fill('')
  const propValues = (props.value || '').slice(0, fields).split('')
  if (propValues && propValues.length) {
    initialValues = []
    for (let i = 0; i < fields; i++) {
      initialValues.push(propValues[i] || '')
    }
  }
  const [codeValues, setCodeValues] = useState<string[]>(initialValues)
  const [inputRefs, setInputRefs] = useState<React.MutableRefObject<HTMLInputElement | null>[]>([])
  const [isUpdating, setIsUpdating] = useState<boolean>(false)

  useEffect(() => {
    setInputRefs((pastRefs) =>
      Array(6)
        .fill(null)
        .map((_, i) => pastRefs[i] || createRef()),
    )
  }, [])

  const triggerChange = async (values = codeValues) => {
    const { onChange, onComplete } = props
    const code = values.join('')
    onChange && onChange(code)
    if (onComplete && code.length >= fields) {
      setIsUpdating(true)
      await onComplete(code)
      setIsUpdating(false)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const index = parseInt(e.currentTarget.name)
    const prevIndex = index - 1
    const nextIndex = index + 1
    const prev = inputRefs[prevIndex]
    const next = inputRefs[nextIndex]
    const vals = [...codeValues]
    switch (e.key) {
      case 'Backspace':
        e.preventDefault()
        if (codeValues[index]) {
          vals[index] = ''
          setCodeValues(vals)
          triggerChange(vals)
        } else if (prev) {
          vals[prevIndex] = ''
          prev?.current?.focus()
          setCodeValues(vals)
          triggerChange(vals)
        }
        break
      case 'ArrowLeft':
        e.preventDefault()
        if (prev) {
          prev?.current?.focus()
        }
        break
      case 'ArrowRight':
        e.preventDefault()
        if (next) {
          next?.current?.focus()
        }
        break
      case 'ArrowUp':
      case 'ArrowDown':
        e.preventDefault()
        break
      default:
        break
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const index = parseInt(e.target.name)
    e.target.value = e.target.value.replace(/[^\d]/gi, '')
    if (e.target.value === '') {
      return
    }
    let next
    const value = e.target.value
    let values = codeValues
    values = Object.assign([], values)
    if (value.length > 1) {
      let nextIndex = value.length + index - 1
      if (nextIndex >= fields) {
        nextIndex = fields - 1
      }
      next = inputRefs[nextIndex]
      const split = value.split('')
      split.forEach((item, i) => {
        const cursor = index + i
        if (cursor < fields) {
          values[cursor] = item
        }
      })
      setCodeValues(values)
    } else {
      next = inputRefs[index + 1]
      values[index] = value
      setCodeValues(values)
    }

    if (next) {
      next?.current?.focus()
      next?.current?.select()
    }

    triggerChange(values)
  }

  let selectedIndex = codeValues.findIndex((v) => !v)
  selectedIndex = typeof selectedIndex !== 'undefined' ? selectedIndex : codeValues.length - 1

  const handleResendCode = async () => {
    const { onResend } = props
    if (onResend) {
      setCodeValues(Array(fields).fill(''))
      setIsUpdating(true)
      inputRefs?.[0]?.current?.focus()
      await onResend()
      setIsUpdating(false)
    }
  }

  return (
    <form onSubmit={() => props.onComplete(codeValues.join(''))}>
      <div className="row mb-3">
        <div className="col text-center">
          <label>Enter the verification code we sent to your email</label>
          <div>
            <span className="fa-layers fa-fw fa-3x">
              <FontAwesomeIcon icon={faEnvelope} />
              <span className="fa-layers-counter bg-primary">{'\u00A0'}</span>
            </span>
          </div>
          <div className="input-group mt-5 mb-3">
            {codeValues.map((value, index) => (
              <input
                name={index?.toString()}
                autoComplete="one-time-code"
                type="tel"
                className={`form-control form-control-lg text-center${
                  props.has_error ? ' border-warning border' : ''
                }`}
                pattern="[0-9]*"
                autoFocus={index === selectedIndex}
                key={['code', index].join(':')}
                value={value}
                ref={inputRefs[index]}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                onFocus={(e) => e.target.select()}
                disabled={props.disabled || isUpdating}
                required
              />
            ))}
          </div>
          {props.onResend && (
            <div>
              <button type="button" className="btn btn-link" onClick={() => handleResendCode()}>
                <FontAwesomeIcon icon={faUndo} className="me-2" />
                Resend code
              </button>
            </div>
          )}
          {props.onPrevious && (
            <div className="button-container d-flex justify-content-between mt-4">
              <button
                type="button"
                className="btn btn-link back-btn m-1 me-auto"
                onClick={(): void => {
                  if (props.onPrevious) props.onPrevious()
                }}
              >
                Back
              </button>
            </div>
          )}
        </div>
      </div>
    </form>
  )
}
